import "@xyflow/react/dist/style.css";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ContextGraphCanvas from "../components/ContextGraph/ContextGraphCanvas";
import ContextGraphLegend from "../components/ContextGraph/ContextGraphLegend";
import ContextGraphNodePanel from "../components/ContextGraph/ContextGraphNodePanel";
import { PdfViewerProvider } from "../context/PdfViewerContext";
import { SpreadsheetViewerProvider } from "../context/SpreadsheetViewerContext";
import ContextGraphAnswerView from "../components/ContextGraph/ContextGraphAnswerView";
import ContextGraphScopeControls from "../components/ContextGraph/ContextGraphScopeControls";
import { type AnswerScope } from "../components/ContextGraph/ContextGraphScopeToggle";
import {
	collapseGraph,
	collapsedIds,
	isCollapsedNode,
} from "../components/ContextGraph/collapse";
import "../components/ContextGraph/contextGraph.css";
import ErrorDisplay from "../components/ErrorDisplay";
import { chatReturnLink } from "../utils/contextGraphLink";
import { useDopplerUriOpener } from "../hooks/useDopplerUriOpener";
import type { ProjectItem } from "../services/api";
import {
	contextGraphService,
	type ContextGraphResult,
} from "../services/contextGraphService";
import { projectService } from "../services/projectService";
import type { ContextGraph as ContextGraphData, ContextNode } from "../types/contextGraph";
import BackButton from "../components/BackButton";

type LoadState =
	| { phase: "loading" }
	| { phase: "ready"; graph: ContextGraphData }
	| { phase: "unavailable" }
	| { phase: "error" }
	/** Neither `?project=` nor `?chat=` was given. */
	| { phase: "unscoped" };

/** The `scope` query value that widens an answer's URL to its conversation. */
const CHAT_SCOPE = "chat";

/** Stable identity, so the room's controls do not re-render on every pass. */
const EMPTY_COMPANIES: readonly ContextNode[] = [];


/**
 * The room the sidebar entry lands on: the most recently touched one.
 *
 * `/api/v3/projects` does not promise an order, so "most recent" is derived
 * here from the timestamps it does promise, with list order as the tiebreak.
 */
function mostRecentRoom(rooms: ProjectItem[]): ProjectItem | null {
	let best: ProjectItem | null = null;
	let bestAt = Number.NEGATIVE_INFINITY;
	for (const room of rooms) {
		const at = Date.parse(room.updated_at ?? room.created_at ?? "");
		const rank = Number.isNaN(at) ? Number.NEGATIVE_INFINITY : at;
		if (best === null || rank > bestAt) {
			best = room;
			bestAt = rank;
		}
	}
	return best;
}

/**
 * Screen 4 — the context graph behind an answer.
 *
 * Three scopes, chosen by query params: `?project=` shows a whole deal room;
 * `?chat=&message=` shows only what one answer's verified citations touched;
 * `?chat=&message=&scope=chat` (or a bare `?chat=`) shows the whole
 * conversation — every answer's graph in the chat, unioned. The message id is
 * kept in the URL when the reader widens to the conversation, so "This answer"
 * can narrow back and the way back to the chat still lands on the paragraph
 * they left. A bare `/context-graph` is the sidebar entry — it resolves the user's most
 * recent deal room and rewrites itself to `?project=`, so every state of this
 * page is a URL someone can share. `?fixture=1` renders the canonical fixture
 * in development, which is how the view is reviewed before the backend
 * projections land.
 *
 * Gated by `REACT_APP_CABINET_UI_ENABLED` at the route level (the same flag as
 * `/cabinet` — it is the same catalog seen a second way), so the page itself
 * assumes it is reachable.
 */
const ContextGraphPageBody: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const openHref = useDopplerUriOpener();
	const [state, setState] = useState<LoadState>({ phase: "loading" });
	const [selected, setSelected] = useState<ContextNode | null>(null);
	const [reloadToken, setReloadToken] = useState(0);
	/** Synthetic "+N" cards the reader has opened. Reset with the graph. */
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
	const [expandAll, setExpandAll] = useState(false);

	const projectId = searchParams.get("project");
	const focusParam = searchParams.get("focus");
	const chatId = searchParams.get("chat");
	const messageId = searchParams.get("message");
	const fixture = searchParams.get("fixture") === "1";
	// A chat with no message to narrow to can only be read whole.
	const chatScope =
		Boolean(chatId) && (searchParams.get("scope") === CHAT_SCOPE || !messageId);
	const messageScope = Boolean(chatId && messageId) && !chatScope;
	/** Either reading of a chat: an answer, or the conversation around it. */
	const answerScope = messageScope || chatScope;
	const backHref = answerScope ? chatReturnLink(chatId, messageId) : null;

	// The deal rooms, for the header selector and for the room a bare
	// `/context-graph` (the sidebar entry) lands on. `null` = still resolving.
	const [rooms, setRooms] = useState<ProjectItem[] | null>(null);
	useEffect(() => {
		// A message neighbourhood is not switchable by room, and the fixture
		// deliberately never touches the API.
		if (answerScope || fixture) return;
		let cancelled = false;
		void projectService
			.listProjects()
			.then((response) => {
				if (!cancelled) setRooms(response.projects ?? []);
			})
			.catch(() => {
				if (!cancelled) setRooms([]);
			});
		return () => {
			cancelled = true;
		};
	}, [answerScope, fixture]);

	// `replace`, so the graph does not sit between the sidebar click and the
	// page the user was on before it.
	useEffect(() => {
		if (answerScope || fixture || projectId || rooms === null) return;
		const room = mostRecentRoom(rooms);
		if (room) setSearchParams({ project: room.id }, { replace: true });
	}, [answerScope, fixture, projectId, rooms, setSearchParams]);

	useEffect(() => {
		// An answer's graph — one reply's, or the conversation's — is loaded by
		// `ContextGraphAnswerView`, which the rail mounts too. The page only
		// still loads what is its own: a deal room, and the fixture.
		if (answerScope) return;
		let cancelled = false;
		setState({ phase: "loading" });
		setSelected(null);
		setExpanded(new Set());

		const request: Promise<ContextGraphResult> | null = projectId
			? contextGraphService.getRoomGraph(projectId)
			: // Fixture mode short-circuits inside the service, so a bare
				// `?fixture=1` still needs *a* call to reach it.
				fixture
				? contextGraphService.getRoomGraph("fixture")
				: null;

		if (request === null) {
			setState({ phase: "unscoped" });
			return;
		}

		void request.then((result) => {
			if (cancelled) return;
			if (result.status === "ok") setState({ phase: "ready", graph: result.graph });
			else if (result.status === "unavailable") setState({ phase: "unavailable" });
			else setState({ phase: "error" });
		});

		return () => {
			cancelled = true;
		};
	}, [answerScope, projectId, fixture, reloadToken]);

	const retry = useCallback(() => setReloadToken((token) => token + 1), []);

	const toggleExpanded = useCallback((id: string) => {
		setExpanded((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const handleSelect = useCallback(
		(node: ContextNode) => {
			// A "+N" card is a door, not a citation: opening it is the whole
			// interaction, so it never opens the side panel instead.
			if (isCollapsedNode(node)) {
				toggleExpanded(node.id);
				return;
			}
			// Every card opens its panel: a figure shows the value, the quote,
			// the document and the page it came from, with the way into the PDF
			// one button further. Jumping straight into the PDF skipped exactly
			// the provenance a reader clicks a card to see.
			setSelected(node);
		},
		[toggleExpanded],
	);

	/** Into the in-app viewer, with the document's name on the header. */
	const openCitation = useCallback(
		(href: string, filename?: string) => openHref(href, { chatId, filename }),
		[openHref, chatId],
	);

	const graph = state.phase === "ready" ? state.graph : null;

	/**
	 * The focus comes from the URL so a neighbourhood is shareable, and falls
	 * back to whatever the API centred the graph on. `?focus=` present but empty
	 * is the reader explicitly asking for the whole answer.
	 */
	const focus = focusParam !== null ? focusParam || null : (graph?.focus ?? null);

	/**
	 * The same value for the view, which resolves the fallback itself: it is
	 * the one holding the graph now, so `undefined` — nobody has chosen — is
	 * what it has to be told, not a focus guessed without a graph.
	 */
	const chosenFocus = focusParam !== null ? focusParam || null : undefined;

	const setFocus = useCallback(
		(next: string | null) => {
			const params = new URLSearchParams(searchParams);
			// Always written, even when empty: an absent `focus` means "use the
			// API's", which is not the same as "the reader chose all of it".
			params.set("focus", next ?? "");
			setSearchParams(params, { replace: true });
			setExpanded(new Set());
			setSelected(null);
		},
		[searchParams, setSearchParams],
	);

	/**
	 * Step between one answer and its conversation. The message id stays in
	 * the URL either way; only `scope` comes and goes, so both readings are
	 * shareable and the reader can return to the answer they started from.
	 */
	const setAnswerScope = useCallback(
		(next: AnswerScope) => {
			const params = new URLSearchParams(searchParams);
			if (next === CHAT_SCOPE) params.set("scope", CHAT_SCOPE);
			else params.delete("scope");
			setSearchParams(params, { replace: true });
			setSelected(null);
		},
		[searchParams, setSearchParams],
	);

	// What is actually drawn: the answer narrowed to one company and folded
	// down to the cards that carry it. The full graph stays untouched, so the
	// company list and a synthetic card's members can both be resolved from it.
	const shown = useMemo(
		() => (graph ? collapseGraph(graph, { focus, expanded, expandAll }) : null),
		[graph, focus, expanded, expandAll],
	);

	// Never over a room: narrowing to one company is a question about an
	// ANSWER — "which borrower is this bit about" — and the room graph is
	// already the whole room by definition; the room selector beside it is the
	// control that belongs here. (The answer view offers its own picker.)
	const companies: readonly ContextNode[] = EMPTY_COMPANIES;

	/** The real nodes a selected "+N" card stands in for. */
	const members = useMemo(() => {
		if (!graph || !selected) return [];
		const ids = new Set(collapsedIds(selected));
		return graph.nodes.filter((node) => ids.has(node.id));
	}, [graph, selected]);

	return (
		// `context-graph` carries the palette's custom properties, so it has to
		// sit above the legend and the side panel too — not only the canvas.
		<div className="context-graph flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
			<header className="flex items-start justify-between gap-4 px-6 pb-3 pt-6">
				<div>
					{/* The way back. Only in message scope: a room graph IS the
					    sidebar destination, but an answer's neighbourhood is a
					    detour off a conversation, and the reader has to be able
					    to return to the paragraph they left. */}
					{backHref && (
						<BackButton
							to={backHref}
							label="Back to the conversation"
							className="mb-1"
						/>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-2">
				{/* The answer / conversation toggle and the company picker travel
				    with the graph they act on — they are inside the answer view
				    now, which the Analyst rail mounts as well. What is left here
				    is the room's own chrome. */}
				{!answerScope && state.phase === "ready" && (
					<ContextGraphScopeControls
						companies={companies}
						focus={focus}
						onFocusChange={setFocus}
						expandAll={expandAll}
						onExpandAllChange={setExpandAll}
					/>
				)}
				{/* Only in room scope: an answer's neighbourhood belongs to the chat
				    it came from, and switching room under it would be a lie. */}
				{!answerScope && rooms !== null && rooms.length > 0 && (
					<label className="shrink-0 text-sm text-text-muted">
						<span className="sr-only">Deal room</span>
						<select
							className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-text-base focus:border-[#008782] focus:ring-[#008782]"
							value={projectId ?? ""}
							onChange={(event) =>
								setSearchParams({ project: event.target.value })
							}
						>
							{rooms.map((room) => (
								<option key={room.id} value={room.id}>
									{room.name}
								</option>
							))}
						</select>
					</label>
				)}
				</div>
			</header>

			{answerScope && chatId ? (
				<div className="flex min-h-0 flex-1 flex-col px-6 pb-6">
					<ContextGraphAnswerView
						chatId={chatId}
						messageId={messageId ?? ""}
						scope={chatScope ? "chat" : "message"}
						onScopeChange={setAnswerScope}
						focus={chosenFocus}
						onFocusChange={setFocus}
						onOpenHref={openCitation}
					/>
				</div>
			) : state.phase === "error" ? (
				<ErrorDisplay error={new Error("context-graph-unreachable")} onRetry={retry} />
			) : state.phase === "loading" ? (
				<Skeleton />
			) : state.phase === "unavailable" ? (
				<Notice>Graph projection not available yet.</Notice>
			) : state.phase === "unscoped" ? (
				// No scope in the URL means the sidebar entry: either the default
				// room is still being resolved, or there is no room to resolve to.
				rooms !== null && rooms.length === 0 ? (
					<Notice>
						No deal rooms yet — upload files to a project and its graph appears
						here.
					</Notice>
				) : (
					// Either the room list or the redirect onto it is a frame away.
					<Skeleton />
				)
			) : shown === null || shown.nodes.length === 0 ? (
				<Notice>No graph for this answer yet.</Notice>
			) : (
				<div className="flex min-h-0 flex-1 flex-col px-6 pb-6">
					{/* The surface takes its colour from the same token the edge-label
					    pills use, so a pill actually matches what is behind it. */}
					<div
						className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200"
						style={{ background: "var(--cg-canvas)" }}
					>
						<div className="flex min-w-0 flex-1 flex-col">
							{/* The canvas takes the viewport minus the app header, this
							    page's own header and the legend, so the graph gets the
							    whole screen and the page never scrolls at 982px. */}
							<div className="min-h-[380px] flex-1">
								<ContextGraphCanvas
									graph={shown}
									focus={focus}
									onSelectNode={handleSelect}
								/>
							</div>
							<ContextGraphLegend graph={shown} />
						</div>

						{selected && (
							<div className="w-[360px] shrink-0">
								<ContextGraphNodePanel
									node={selected}
									members={members}
									onOpenHref={openCitation}
									onClose={() => setSelected(null)}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

const Skeleton: React.FC = () => (
	<div className="px-6 pb-6">
		<div className="h-[520px] animate-pulse rounded-lg border border-gray-200 bg-white" />
	</div>
);

/** A quiet, non-alarming empty state — none of these are failures. */
const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="px-6 pb-6">
		<div className="rounded-lg border border-gray-200 bg-white px-6 py-10 text-center text-sm text-text-muted">
			{children}
		</div>
	</div>
);

/**
 * The in-app viewers are mounted HERE, not app-wide, the same way the chat
 * and KPI pages mount their own. Without them `useDopplerUriOpener` falls back
 * to a signed URL in a new tab — which is what a card click used to do: the
 * page opened, the header had no name, and the cited words were not
 * highlighted, because the highlight lives in the in-app viewer.
 */
const ContextGraphPage: React.FC = () => (
	<SpreadsheetViewerProvider>
		<PdfViewerProvider>
			<ContextGraphPageBody />
		</PdfViewerProvider>
	</SpreadsheetViewerProvider>
);

export default ContextGraphPage;
